import { observer, useLocalObservable } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { TemplateFormat } from "../Playground/types";
import { AIJSONSchemaAssistant } from "../ui/AIJSONSchemaAssistant";
import { Button } from "../ui/button";
import CompareColumnLayout from "../ui/CompareColumnLayout";

import { INTERACTIVE_EDITOR_DEFAULT_SCHEMA } from "./constants";
import FormatChangeConfirmation from "./FormatChangeConfirmation";
import FormatWarningBanner from "./FormatWarningBanner";
import InteractiveEditor from "./InteractiveEditor";
import OneOfStructuredOutputWarning from "./OneOfStructuredOutputWarning";
import JsonEditor from "./JsonEditor";
import SchemaEditorStore from "./schema-editor-store";
import {
  SchemaEditorLayoutProvider,
  type SchemaEditorLayoutActions,
} from "./schema-editor-layout-context";

import { SchemaDefinition } from "@/types";

const SchemaEditorStoreContext = React.createContext<SchemaEditorStore | null>(null);

export const SchemaEditorProvider = ({
  value,
  children,
}: {
  value: SchemaEditorStore;
  children: React.ReactNode;
}) => (
  <SchemaEditorStoreContext.Provider value={value}>{children}</SchemaEditorStoreContext.Provider>
);

export const useSchemaEditor = () => {
  const schemaEditorStore = React.useContext(SchemaEditorStoreContext);
  if (!schemaEditorStore) {
    throw new Error("useSchemaEditorStore must be used within a SchemaEditorProvider");
  }
  return schemaEditorStore;
};

interface SchemaEditorProps {
  schema?: object;
  onSubmit?: (schema: SchemaDefinition) => void;
  isFunction?: boolean;
  readonly?: boolean;
  children?: React.ReactNode;
  format?: TemplateFormat;
  miniMode?: boolean;
  messages?: string[];
  submitLabel?: string;
  hideSubmitButton?: boolean;
  actionPosition?: "top" | "bottom";
  headerActions?: React.ReactNode | ((triggerSubmit: () => void) => React.ReactNode);
  aiAssistantPortalContainer?: HTMLElement | null;
  /** When set (e.g. from model parameters), shows a hint if the schema uses `oneOf` on Anthropic/Bedrock Claude paths. */
  structuredOutputProviderName?: string | null;
}

const SchemaEditor = ({
  schema,
  onSubmit,
  isFunction = false,
  children,
  readonly,
  format = "f-string",
  miniMode = false,
  messages,
  submitLabel,
  hideSubmitButton = false,
  actionPosition = "bottom",
  headerActions,
  aiAssistantPortalContainer,
  structuredOutputProviderName,
}: SchemaEditorProps) => {
  const schemaEditorStore = useLocalObservable(
    () =>
      new SchemaEditorStore(
        schema || INTERACTIVE_EDITOR_DEFAULT_SCHEMA,
        isFunction,
        readonly,
        format,
      ),
  );

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [layoutActions, setLayoutActions] = useState<SchemaEditorLayoutActions | null>(null);

  const handleRegisterLayoutActions = useCallback((actions: SchemaEditorLayoutActions) => {
    setLayoutActions(actions);
  }, []);

  useEffect(() => {
    if (schemaEditorStore.format !== format) {
      schemaEditorStore.setFormat(format);
    }
  }, [format, schemaEditorStore]);

  const triggerSubmit = useCallback(() => {
    schemaEditorStore.markSubmitAttempted();
    if (schemaEditorStore.canSubmit) {
      onSubmit?.({
        name: schemaEditorStore.name,
        description: schemaEditorStore.description,
        [schemaEditorStore.isFunction ? "parameters" : "schema"]: schemaEditorStore.definition,
        strict: schemaEditorStore.strict,
      });
    }
  }, [schemaEditorStore, onSubmit]);

  const resolvedHeaderActions =
    typeof headerActions === "function" ? headerActions(triggerSubmit) : headerActions;

  const actionBar = (
    <div
      className={
        miniMode
          ? `pointer-events-none sticky ${actionPosition === "top" ? "top-0 mb-4" : "bottom-0 mt-4"} left-0 right-0 flex w-full flex-row items-center gap-3 border-${actionPosition === "top" ? "b" : "t"} border-gray-200 bg-white pb-2 pt-4`
          : "pointer-events-none fixed bottom-4 left-12 right-3 z-20 flex flex-row justify-end gap-3"
      }
    >
      <div className="pointer-events-auto flex-1">{children}</div>
      <div className="pointer-events-auto flex shrink-0 gap-x-2">
        {!readonly && !hideSubmitButton && (
          <Button size="lg" className="pointer-events-auto" onClick={triggerSubmit}>
            {submitLabel ?? (isFunction ? "Add Function" : "Set Schema")}
          </Button>
        )}
      </div>
    </div>
  );

  const layoutContextValue = useMemo<SchemaEditorLayoutActions>(
    () => ({
      openJsonEditor: () => layoutActions?.openJsonEditor(),
    }),
    [layoutActions],
  );

  return (
    <SchemaEditorProvider value={schemaEditorStore}>
      <SchemaEditorLayoutProvider value={layoutContextValue}>
        <div
          className={`flex h-full min-h-0 w-full gap-4 pb-6 ${miniMode ? "flex-col" : "flex-row"}`}
        >
          {actionPosition === "top" && actionBar}
          <CompareColumnLayout
            onRegisterLayoutActions={handleRegisterLayoutActions}
            banner={
              <>
                {schemaEditorStore.shouldShowFormatWarning && (
                  <FormatWarningBanner setIsConfirmModalOpen={setIsConfirmModalOpen} />
                )}
                <OneOfStructuredOutputWarning
                  providerName={structuredOutputProviderName}
                  schema={schemaEditorStore.definition}
                />
              </>
            }
            defaultOpen={
              !schemaEditorStore.isStandardSchema
                ? { json: true }
                : {
                    interactive: true,
                    json: false,
                  }
            }
            disableInteractive={!schemaEditorStore.isStandardSchema}
            columnNames={["Interactive Editor", "JSON Editor"]}
            leftComponent={
              <InteractiveEditor
                onConfirmFormat={() => {
                  setIsConfirmModalOpen(true);
                }}
              />
            }
            rightComponent={<JsonEditor />}
            aiAssistant={
              <AIJSONSchemaAssistant
                onSuccess={data => {
                  schemaEditorStore.applyJsonToState(data);
                }}
                messages={messages}
                popoverPortalContainer={aiAssistantPortalContainer}
              />
            }
            headerActions={resolvedHeaderActions}
          />
          {actionPosition === "bottom" && actionBar}
        </div>
        <FormatChangeConfirmation isOpen={isConfirmModalOpen} setIsOpen={setIsConfirmModalOpen} />
      </SchemaEditorLayoutProvider>
    </SchemaEditorProvider>
  );
};

export default observer(SchemaEditor);
